import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  DeviceTypeProps,
  DeviceTypes,
  ENVTYPES,
} from "utils/model/CommonTypes";

interface LoaderItem {
  name: string;
  message?: string | string[];
}

interface StateContextType {
  dmTitle: string;
  setDmTitle: (v: string) => void;
  ENVTYPES: ENVTYPES;
  deviceType: DeviceTypeProps | "";
  setPageLoader: (
    name: string,
    running: boolean,
    message?: string | string[]
  ) => void;
  loader: Array<LoaderItem> | null;
  loaderMessage: string | string[] | null;
  inActiveSignOut: boolean;
  setInActiveSignOut: (v: boolean) => void;
  refreshKey: number;
  setRefreshKey: (v: number) => void;
  refreshAccordion: () => void;
  isShowErrorMessage: boolean;
  HideErrorMessage: () => void;
}

export const StateContext = createContext<StateContextType>({} as any);
interface Prop {
  children: React.ReactNode;
}

export const StateProvider = (prop: Prop) => {
  const [deviceType, setDeviceType] = useState<DeviceTypeProps | "">("");
  const [loader, setLoader] = useState<Array<LoaderItem> | null>(null);
  const [loaderMessage, setLoaderMessage] = useState<string | string[] | null>(
    null
  );
  const [inActiveSignOut, setInActiveSignOut] = useState(false);
  const [dmTitle, setDmTitle] = useState<string>("");
  const [refreshKey, setRefreshKey] = useState(0);
  const ENVTYPES = {
    BASE_URL: process.env.REACT_APP_BASE_URL,
    OPC_APIM_SUB_KEY: process.env.REACT_APP_OPC_APIM_SUB_KEY,
  } as ENVTYPES;

  const isHidden = sessionStorage.getItem("HideServiceNowMessage");

  const [isShowErrorMessage, setShowErrorMessage] = useState<boolean>(isHidden !== "true");

  const HideErrorMessage = () => {
    sessionStorage.setItem("HideServiceNowMessage", "true");
    setShowErrorMessage(false);
  };

  const handleResize = () => {
    if (window.innerWidth >= 992) {
      setDeviceType(DeviceTypes.DESKTOP);
    } else if (window.innerWidth >= 768) {
      setDeviceType(DeviceTypes.TABLET);
    } else {
      setDeviceType(DeviceTypes.MOBILE);
    }
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const setPageLoader = (
    name: string,
    running: boolean,
    message?: string | string[]
  ) => {
    const _loaders = loader ? [...loader] : [];
    if (running) {
      _loaders.push({ name, message });
      setLoaderMessage(message || null);
    } else {
      const index = _loaders.findIndex((l) => l.name === name);
      if (index > -1) {
        _loaders.splice(index, 1);
      }
      setLoaderMessage(
        _loaders.length > 0 && _loaders[_loaders.length - 1].message
          ? _loaders[_loaders.length - 1].message || null
          : null
      );
    }
    setLoader(_loaders.length ? _loaders : null);
  };

  const refreshAccordion = () => {
    setRefreshKey((prevKey) => prevKey + 1);
  };

  const value = useMemo(
    () => ({
      dmTitle,
      setDmTitle,
      ENVTYPES,
      deviceType,
      setPageLoader,
      loader,
      loaderMessage,
      inActiveSignOut,
      setInActiveSignOut,
      refreshKey,
      setRefreshKey,
      refreshAccordion,
      isShowErrorMessage,
      HideErrorMessage,
    }),
    [ENVTYPES, loader, loaderMessage, deviceType, inActiveSignOut, isShowErrorMessage]
  );

  return (
    <StateContext.Provider value={value}>{prop.children}</StateContext.Provider>
  );
};

export const useGlobalState = () => {
  return useContext(StateContext);
};
